/* eslint-disable yoda */
import { arrayMove } from '@dnd-kit/sortable';
import { MINIMUM_REQUIRED_PHOTOS } from '../constants/actions.consts';

/**
 * Checks if the number of photo-type media items meets the minimum required count.
 * (Excludes 'video' and '3d' embeds from the count).
 * * @param {Array<object>} media - The current array of all media items.
 * @param {number} requiredCount - The minimum number of photos required.
 * @returns {boolean} True if the number of images (non-embeds) is sufficient.
 */
export const isMinimumPhotoRequirementMet = (media) => {
  const photoCount = media
    .filter(item => item.type !== 'video' && item.type !== '3d').length;

  return photoCount >= MINIMUM_REQUIRED_PHOTOS;
};

/**
 * Checks if a given media item is a video or 3D tour embed.
 * @param {object} mediaItem - The item object from the media array.
 * @returns {boolean} True if the item type is 'video' or '3d', false otherwise.
 */
export const isEmbedMedia = (mediaItem) => {
  if (!mediaItem || !mediaItem.type) {
    return false;
  }
  const type = mediaItem.type;

  return type === 'video' || type === '3d';
};

/**
 * Re-prioritizes the media list after deletion to ensure a photo is in the cover spot (index 0).
 * Reassigns the 'cover' type and updates sortOrder for all items.
 * * @param {Array<object>} currentMedia The array of media items BEFORE deletion.
 * @param {string} deletedItemId The ID of the item to be removed.
 * @returns {Array<object>} The new, sorted, and formatted media array.
 */
export const enforceCoverPhotoRule = (currentMedia, deletedItemId) => {
  const tempMedia = currentMedia.filter(item => item.id !== deletedItemId);

  const firstItem = tempMedia[0];
  const firstPhotoIndex = tempMedia.findIndex(item => !isEmbedMedia(item));

  if (firstItem && (isEmbedMedia(firstItem)) && firstPhotoIndex !== -1) {
    const swappedMedia = arrayMove(tempMedia, firstPhotoIndex, 0);
    tempMedia.splice(0, tempMedia.length, ...swappedMedia);
  }

  const finalMedia = tempMedia.map((item, index) => {
    const newSortOrder = index + 1;
    let newType = item.type;

    if (index === 0 && item.type === '') {
      newType = 'cover';
    } else if (newType === 'cover') {
      newType = '';
    }

    return {
      ...item,
      sortOrder: newSortOrder,
      type: newType
    };
  });

  return finalMedia;
};

/**
 * Calculates the next sequential sortOrder available in the media array for video and 3d your only
 * prevents video and 3d tours from taking up the first sortOrder (sortOrder = 1).
 * @param {Array<object>} media The current media array from the store.
 * @returns {number} The next available sort order (1 + max existing sortOrder, or 2 if empty).
 */
export const getNextSortOrder = (media) => {
  if (media.length === 0) {
    return 2;
  }

  const maxOrder = media.reduce((max, item) => {
    return item.sortOrder > max ? item.sortOrder : max;
  }, 0);

  return maxOrder + 1;
};
